import { useId } from 'react';
import { AlertTriangle, Inbox, RefreshCw, WifiOff } from 'lucide-react';
import { initials } from '../lib/format.js';

export function Spinner({ label = 'Carregando' }) {
  return <span className="spinner" role="status" aria-label={label} />;
}

export function Button({
  variant = 'secondary',
  size,
  block,
  loading,
  icon: Icon,
  children,
  className = '',
  disabled,
  ...props
}) {
  const cls = [
    'btn',
    variant !== 'secondary' && `btn-${variant}`,
    size === 'sm' && 'btn-sm',
    block && 'btn-block',
    className,
  ]
    .filter(Boolean)
    .join(' ');
  return (
    <button
      type="button"
      className={cls}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...props}
    >
      {loading ? <Spinner label="Salvando" /> : Icon ? <Icon aria-hidden="true" /> : null}
      {children}
    </button>
  );
}

export function IconButton({ icon: Icon, label, badge, className = '', ...props }) {
  return (
    <button type="button" className={`icon-btn ${className}`} aria-label={label} title={label} {...props}>
      <Icon aria-hidden="true" />
      {badge ? <span className="dot">{badge > 99 ? '99+' : badge}</span> : null}
    </button>
  );
}

/** Campo com rótulo, dica e erro corretamente associados (acessibilidade). */
export function Field({ label, hint, error, optional, children, className = '' }) {
  const id = useId();
  const hintId = hint ? `${id}-hint` : undefined;
  const errId = error ? `${id}-err` : undefined;
  const describedBy = [hintId, errId].filter(Boolean).join(' ') || undefined;
  const child =
    typeof children === 'function'
      ? children({ id, 'aria-describedby': describedBy, 'aria-invalid': error ? true : undefined })
      : children;
  return (
    <div className={`field ${className}`}>
      {label && (
        <label htmlFor={id}>
          {label}
          {optional && <span className="field-optional"> (opcional)</span>}
        </label>
      )}
      {child}
      {hint && !error && (
        <span className="field-hint" id={hintId}>
          {hint}
        </span>
      )}
      {error && (
        <span className="field-error" id={errId} role="alert">
          {error}
        </span>
      )}
    </div>
  );
}

export function Input({ label, hint, error, optional, className, ...props }) {
  return (
    <Field label={label} hint={hint} error={error} optional={optional} className={className}>
      {(a) => <input className="input" {...a} {...props} />}
    </Field>
  );
}

export function Textarea({ label, hint, error, optional, className, ...props }) {
  return (
    <Field label={label} hint={hint} error={error} optional={optional} className={className}>
      {(a) => <textarea className="textarea" {...a} {...props} />}
    </Field>
  );
}

export function Select({ label, hint, error, optional, options, placeholder, className, ...props }) {
  return (
    <Field label={label} hint={hint} error={error} optional={optional} className={className}>
      {(a) => (
        <select className="select" {...a} {...props}>
          {placeholder !== undefined && <option value="">{placeholder}</option>}
          {options.map((o) => (
            <option key={o.value} value={o.value} disabled={o.disabled}>
              {o.label}
            </option>
          ))}
        </select>
      )}
    </Field>
  );
}

export function Checkbox({ label, hint, ...props }) {
  return (
    <label className="check">
      <input type="checkbox" {...props} />
      <span>
        {label}
        {hint && (
          <span className="field-hint" style={{ display: 'block' }}>
            {hint}
          </span>
        )}
      </span>
    </label>
  );
}

export function Badge({ tone, icon: Icon, children, title }) {
  return (
    <span className={`badge${tone ? ` badge-${tone}` : ''}`} title={title}>
      {Icon && <Icon aria-hidden="true" />}
      {children}
    </span>
  );
}

export function Avatar({ name, size, color }) {
  return (
    <span
      className={`avatar${size ? ` avatar-${size}` : ''}`}
      style={color ? { background: color, color: '#fff' } : undefined}
      aria-hidden="true"
    >
      {initials(name)}
    </span>
  );
}

export function Card({ title, icon: Icon, action, children, className = '', as: Tag = 'section', ...props }) {
  return (
    <Tag className={`card ${className}`} {...props}>
      {(title || action) && (
        <div className="card-header">
          {title && (
            <h2>
              {Icon && <Icon aria-hidden="true" />}
              <span className="truncate">{title}</span>
            </h2>
          )}
          {action}
        </div>
      )}
      {children}
    </Tag>
  );
}

export function EmptyState({ icon: Icon = Inbox, title, children, action, compact }) {
  return (
    <div className={`state${compact ? ' state-compact' : ''}`}>
      <span className="state-icon">
        <Icon aria-hidden="true" />
      </span>
      {title && <h3>{title}</h3>}
      {children && <p>{children}</p>}
      {action}
    </div>
  );
}

export function ErrorState({ error, onRetry, compact }) {
  const offline = error?.isNetwork;
  return (
    <div className={`state is-error${compact ? ' state-compact' : ''}`} role="alert">
      <span className="state-icon">
        {offline ? <WifiOff aria-hidden="true" /> : <AlertTriangle aria-hidden="true" />}
      </span>
      <h3>{offline ? 'Sem conexão' : 'Não foi possível carregar'}</h3>
      <p>{error?.message ?? 'Tente novamente em instantes.'}</p>
      {onRetry && (
        <Button icon={RefreshCw} onClick={onRetry}>
          Tentar de novo
        </Button>
      )}
    </div>
  );
}

export function SkeletonRows({ rows = 3 }) {
  return (
    <ul className="list" aria-busy="true" aria-label="Carregando">
      {Array.from({ length: rows }, (_, i) => (
        <li key={i} className="row">
          <span className="skeleton" style={{ width: 24, height: 24, borderRadius: '50%' }} />
          <div className="row-main stack" style={{ gap: 6 }}>
            <span className="skeleton" style={{ width: `${60 - i * 8}%` }} />
            <span className="skeleton" style={{ width: '30%', height: 10 }} />
          </div>
        </li>
      ))}
    </ul>
  );
}

/** Renderiza carregando / erro / vazio / conteúdo de uma consulta do TanStack Query. */
export function QueryView({ query, empty, isEmpty, rows = 3, children, compact }) {
  if (query.isPending) return <SkeletonRows rows={rows} />;
  if (query.isError && !query.data)
    return <ErrorState error={query.error} onRetry={() => query.refetch()} compact={compact} />;
  if (isEmpty?.(query.data)) return empty ?? null;
  return children(query.data);
}

export function Segmented({ value, onChange, options, label }) {
  return (
    <div className="segmented" role="tablist" aria-label={label}>
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="tab"
          aria-selected={value === o.value}
          onClick={() => onChange(o.value)}
        >
          {o.icon && <o.icon aria-hidden="true" size={16} />}
          {o.label}
          {o.count ? <span className="badge badge-accent">{o.count}</span> : null}
        </button>
      ))}
    </div>
  );
}

export function PageHeader({ title, subtitle, actions, children }) {
  return (
    <header className="page-header">
      <div className="page-title">
        <h1>{title}</h1>
        {subtitle && <p className="page-subtitle">{subtitle}</p>}
      </div>
      {actions && <div className="page-actions">{actions}</div>}
      {children}
    </header>
  );
}

export function LoadMore({ query }) {
  if (!query.hasNextPage) return null;
  return (
    <div style={{ padding: 'var(--s-3)', textAlign: 'center' }}>
      <Button size="sm" loading={query.isFetchingNextPage} onClick={() => query.fetchNextPage()}>
        Carregar mais
      </Button>
    </div>
  );
}
